import { ArrowRight, History } from "lucide-react";
import { useUnitsAndCurrency } from "@/hooks/useUnitsAndCurrency";
import { useNumericCell } from "../../hooks/useNumericCell";
import { useCellNavigation } from "../../utils/cellNavigation";
import type { PriceCellProps } from "./types";
import { CELL_INPUT } from "./styles";
import { cn } from "@/lib/utils";
import {
  AI_PENDING_CELL_CLASS,
  getAiChangeValues,
  isAiPendingField,
} from "./AiChangeBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@stockline/ui/components/tooltip";
import { useProductLookups } from "../../contexts/ProductLookupsContext";
import { formatDecimal } from "../../utils/decimalUtils";
import { PreOrderCategory } from "@/app/api/types/preOrder";

export function PriceCell({
  flow,
  preOrder,
  disabled,
  onFlowUpdate,
  proposalDiff,
}: Readonly<PriceCellProps>) {
  const { currencySymbol, pricePerWeightUnit } = useUnitsAndCurrency();
  const priceUnitLabel =
    flow.unit?.price_per_unit && flow.unit?.abbreviation
      ? `${currencySymbol}/${flow.unit.abbreviation}`
      : pricePerWeightUnit;

  const aiChange = getAiChangeValues(flow, "pricePerWeight");
  const aiPending = isAiPendingField(flow, "pricePerWeight");
  // Explicit check on `aiChange` rather than `aiChange?.to ?? ...`: `to` can
  // be `null` (AI legitimately proposes clearing the price), which `??`
  // would treat as absent and fall through to the legacy proposalDiff.
  const proposedRaw = aiChange
    ? aiChange.to
    : (proposalDiff?.fieldDiffs.price?.newValue ??
      proposalDiff?.fieldDiffs.price_per_weight?.newValue);
  const proposedNum =
    proposedRaw != null && proposedRaw !== "" ? Number(proposedRaw) : null;
  // Treat a committed 0 the same as `null`: legacy flows landed in the DB
  // with `price_per_weight = 0` (default-stamped, not user-entered), and
  // showing that 0 — instead of the ERP/partner-group preview — was the
  // user-visible bug. New user-typed zeros never reach the DB because
  // `useNumericCell.handleBlur` no-ops when result equals current value,
  // so demoting 0 to "no commit" is safe both directions.
  const committedPrice =
    flow.price_per_weight != null && flow.price_per_weight !== 0
      ? flow.price_per_weight
      : null;
  const effectivePrice =
    proposedNum != null && Number.isFinite(proposedNum)
      ? proposedNum
      : committedPrice;
  const showOldStrike =
    (aiPending || proposalDiff != null) &&
    proposedNum != null &&
    Number.isFinite(proposedNum) &&
    committedPrice != null &&
    Number(committedPrice) !== proposedNum;

  // Phantom-price preview: when the flow has no committed price (null/undef
  // — explicit 0 counts as user-set) and there's no AI override staged,
  // resolve from the partner/group/product hierarchy and render it inside
  // the input as the native HTML placeholder, with a `(erp)`/`(liste)`
  // source hint folded into the same string. Typing replaces both at once
  // — clean override semantics, no separate badge slot in the cell.
  // Picking a product never writes price_per_weight anymore, so most
  // freshly-created flows land here.
  const lookups = useProductLookups();
  const effectivePartnerId = flow.partner_id ?? preOrder.partner_id ?? null;
  const hasUserPrice = committedPrice != null;
  // Phantom preview is customer-side only: the lookup walks the
  // user-partner → user-group → company-partner → company-group → default
  // product-price hierarchy, all keyed against the sales catalog. On a
  // purchase pre-order the partner is a supplier, so any tier that
  // resolves would surface the sales price as a suggested purchase price
  // — confusing and wrong. Suppress the preview for purchase rows; user
  // types the supplier price directly. Sales/transport stays unchanged.
  const isPurchase = preOrder.category === PreOrderCategory.PURCHASES;
  const previewEligible = !hasUserPrice && proposedNum == null && !isPurchase;
  const preview =
    previewEligible && flow.product_id
      ? (lookups?.getEffectivePriceWithSource?.(
          flow.product_id,
          effectivePartnerId,
        ) ?? null)
      : null;
  // Source tag lives inside the input as part of the placeholder so the
  // preview reads as one composed hint ("2.50 (ERP)") instead of pill +
  // value living in separate slots. Typing clears both at once, which
  // matches "your value replaces the preview" semantics. The recap UI
  // surfaces every resolved price as "ERP" regardless of which tier
  // resolved it (user-partner / user-group / partner / group / product) —
  // the granular `EffectivePrice.source` is still available for future
  // audit surfaces.
  const placeholderText = preview
    ? `${formatDecimal(preview.value)} (ERP)`
    : "0";

  const { localValue, handleChange, handleBlur } = useNumericCell(
    effectivePrice,
    flow.id,
    "price_per_weight",
    onFlowUpdate,
  );

  const handleKeyDown = useCellNavigation();

  const input = (
    <input
      type="text"
      inputMode="decimal"
      data-testid="flow-price-input"
      data-tab-cell
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholderText}
      disabled={disabled}
      className={cn(
        CELL_INPUT,
        showOldStrike && "text-violet-700 font-medium",
        // Bump the preview placeholder a shade darker than the default
        // `placeholder:text-gray-400` so a phantom price actually reads
        // as a number, not as ambient hint text. Italic for "not yours".
        preview && "placeholder:text-gray-600 placeholder:italic",
      )}
    />
  );

  const oldFormatted = String(committedPrice);
  const newFormatted = proposedNum != null ? String(proposedNum) : "";

  // Layout priority: the input (new value) is primary and never gets squeezed
  // by the strike. The strike+arrow live in a min-w-0 overflow-hidden span so
  // they collapse out of layout when the cell is narrow. A tiny History icon
  // is always rendered as a hover affordance — its tooltip surfaces the full
  // "old → new" pair, which is the only way to read the old value when the
  // inline strike has been squeezed out.
  return (
    <div
      className={cn(
        "flex items-center gap-1 min-w-0 w-full",
        aiPending && AI_PENDING_CELL_CLASS,
      )}
    >
      {showOldStrike && (
        <span className="flex items-center gap-1 min-w-0 overflow-hidden">
          <span className="text-xs text-muted-foreground line-through truncate">
            {oldFormatted}
          </span>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
        </span>
      )}
      <span className="flex-1 min-w-0">{input}</span>
      {showOldStrike && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Previous price ${oldFormatted}, now ${newFormatted}`}
                className="shrink-0 inline-flex items-center justify-center h-4 w-4 rounded text-violet-500 hover:text-violet-700 hover:bg-violet-100/60 focus:outline-none focus:ring-1 focus:ring-violet-400"
              >
                <History className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <span className="line-through text-muted-foreground">
                {oldFormatted}
              </span>
              <span className="mx-1">→</span>
              <span className="font-medium">{newFormatted}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <span className="text-[11px] text-gray-700 whitespace-nowrap flex-shrink-0">
        {priceUnitLabel}
      </span>
    </div>
  );
}
